import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { formatCopy, useNamespaceCopy } from '@/i18n/ns';
import { inspectCopy } from '@/i18n/ns/inspect';
import { BottomSheet } from '@/components/ui/sheet';
import { useInspectHost } from './context';
import { bounds } from './geometry';
import { isTypingTarget } from './gestures';
import { PropertySheet } from './property-sheet';
import { MentionTray } from './tray';

/**
 * How much of the layout viewport the on-screen keyboard covers (iOS doesn't resize the
 * layout viewport for it), so the sheet and its Apply stay above the keyboard.
 */
function useKeyboardInset(): number {
  const [inset, setInset] = useState(0);
  useEffect(() => {
    const vv = typeof window !== 'undefined' ? window.visualViewport : null;
    if (!vv) return;
    const update = () => setInset(Math.max(0, window.innerHeight - vv.height - vv.offsetTop));
    update();
    vv.addEventListener('resize', update);
    vv.addEventListener('scroll', update);
    return () => {
      vv.removeEventListener('resize', update);
      vv.removeEventListener('scroll', update);
    };
  }, []);
  return inset;
}

/**
 * The phone's inspector sheet (INSPECTOR-SPEC §2, phone): no hover, so the tap already made
 * the chip; the sheet offers Mention (focus the composer with it), the properties, Select
 * parent and Add another. Targets are 44 px and the sheet keeps above the keyboard and the
 * safe-area inset. Nothing is sent until the person presses Send.
 */
export function InspectSheet({
  onMention,
  onHandoff,
}: {
  onMention?: () => void;
  onHandoff?: () => void;
}) {
  const host = useInspectHost();
  const copy = useNamespaceCopy(inspectCopy);
  const keyboard = useKeyboardInset();
  const [container, setContainer] = useState<HTMLDivElement | null>(null);
  const [sheet, setSheet] = useState<HTMLDivElement | null>(null);
  const [open, setOpen] = useState(true);
  const [chipsOpen, setChipsOpen] = useState(false);
  const returnFocus = useRef<HTMLElement | null>(null);
  const hostRef = useRef(host);
  hostRef.current = host;
  const targetId = host?.session.targets.at(-1)?.targetId;
  const viewportHeight = host?.viewport.height;
  useLayoutEffect(() => {
    if (!container || !open) return;
    const frame = container.closest('[data-testid="artifact-frame"]');
    returnFocus.current =
      frame
        ?.closest('.ap-saved-chrome')
        ?.querySelector<HTMLElement>(':scope > .ap-saved-row [data-testid="inspect-toggle"]') ??
      frame?.querySelector<HTMLElement>('[data-testid="inspect-toggle"]') ??
      null;
  }, [container, open]);
  useEffect(() => {
    setOpen(true);
    setChipsOpen(false);
  }, [host?.session.targets, host?.session.sessionId]);
  useLayoutEffect(() => {
    const element = sheet;
    if (!element || !targetId) return;
    const reveal = () => {
      const current = hostRef.current;
      const layer = element.closest('[data-inspect-layer]');
      const target = current && bounds(current.rectsFor(targetId));
      if (!target || !layer) return;
      const bottom = element.getBoundingClientRect().top - layer.getBoundingClientRect().top - 8;
      if (bottom <= 0 || target.y + target.height <= bottom) return;
      // A target taller than the remaining viewport keeps its start visible. Never change
      // the selected identity or request another hit test to move the content.
      const dy = target.height > bottom ? target.y : target.y + target.height - bottom;
      if (dy > 0) current?.adapter.scrollIntent?.(0, dy);
    };
    reveal();
    const observer = typeof ResizeObserver === 'function' ? new ResizeObserver(reveal) : null;
    observer?.observe(element);
    return () => observer?.disconnect();
  }, [sheet, targetId, viewportHeight, keyboard]);
  // The composer's chips sit under the sheet on a phone, so the sheet carries them (§2, phone).
  if (!host) return null;
  const { session } = host;
  const last = session.targets.at(-1);
  if (!last) return null;
  const handoff = (callback: () => void) => {
    // Release the kit's focus trap before the existing composer handoff. Retain the chips;
    // closing this sheet must neither send them nor return focus over the composer.
    returnFocus.current = null;
    setOpen(false);
    callback();
  };
  const parent = host.adapter.chain(last.nodeId)[1] ?? null;
  const selectParent = async () => {
    if (!parent) return;
    const id = host.request('click');
    if (!id) return;
    const resolved = await session
      .resolve(
        { pid: parent, kind: 'element', rects: host.adapter.rects(parent, id), instanceRef: null },
        id,
      )
      .catch(() => null);
    if (!resolved || !host.clock.isCurrent(id, 'click')) return;
    // The parent replaces the child; a parent that is already selected stays selected.
    session.remove(last.targetId);
    if (!session.targets.some((t) => t.targetId === resolved.targetId))
      host.choose(resolved.targetId, 'add');
  };
  return (
    <div ref={setContainer}>
      <BottomSheet
        open={!!container && open}
        onOpenChange={(next) => {
          if (!next) hostRef.current?.escape();
        }}
        title={formatCopy(copy.selectedCount, { n: String(session.targets.length) })}
        returnFocus={returnFocus}
        testId="inspect-sheet"
        contentProps={{
          ref: setSheet,
          className: 'pc-inspect-sheet',
          portalContainer: container,
          // Keep the whole kit sheet above the keyboard, including its handle/title.
          style: keyboard
            ? { bottom: keyboard, maxHeight: `min(85dvh, calc(100dvh - ${keyboard}px))` }
            : undefined,
          onOpenAutoFocus: (event) => {
            event.preventDefault();
            if (event.target instanceof HTMLElement) event.target.focus();
          },
          onEscapeKeyDown: (event) => {
            event.preventDefault();
            // A text field keeps its existing cancel handler; unchanged typing never clears.
            if (isTypingTarget(event.target instanceof Element ? event.target : null)) return;
            event.stopPropagation();
            hostRef.current?.escape();
          },
        }}
      >
        <button
          type="button"
          className="pc-btn pc-inspect-sheet-count"
          data-variant="quiet"
          aria-expanded={chipsOpen}
          data-testid="inspect-sheet-count"
          onClick={() => setChipsOpen((v) => !v)}
        >
          {formatCopy(copy.selectedToggle, { n: String(session.targets.length) })}{' '}
          {chipsOpen ? '▴' : '▾'}
        </button>
        {chipsOpen && <MentionTray session={session} phone />}
        <p className="pc-inspect-sheet-title">
          {last.label}
          {last.context ? <span className="pc-inspect-note"> · {last.context}</span> : null}
        </p>
        <div className="pc-inspect-sheet-actions">
          {parent && (
            <button
              type="button"
              className="pc-btn"
              data-variant="quiet"
              onClick={() => void selectParent()}
            >
              {copy.selectParent}
            </button>
          )}
          <button
            type="button"
            className="pc-btn"
            data-variant="quiet"
            aria-pressed={host.addMode}
            onClick={() => {
              host.setAddMode(!host.addMode);
              // Let the next real tap reach the layer while retaining this selection.
              if (!host.addMode) setOpen(false);
            }}
          >
            {copy.addAnother}
          </button>
        </div>
        <PropertySheet
          session={session}
          targetIds={session.targets.map((t) => t.targetId)}
          phone
          onHandoff={onHandoff ? () => handoff(onHandoff) : undefined}
          onMention={onMention ? () => handoff(onMention) : undefined}
        />
      </BottomSheet>
    </div>
  );
}
